import type { HotelOffer, HotelSearchInfo } from '../api/hotels.types';
import type { HotelOfferCardModel } from '../types/hotels.presentation';
import { mapOfferToDetailHotel } from './resolveDetailHotel';

/**
 * Maps SSE hotel offers to card models using optional info enrichment.
 *
 * Falls back gracefully:
 *  - hotel name → `hotelId` when no name is available
 *  - rate summary → human fields (name, meal/board, refundability) or i18n placeholder
 *  - total price → 0 when no rate is present
 *  - address → pulled from provider master info when available
 *  - info.destination provides context but is not required
 *
 * Never exposes provider tokens/keys in any visible field.
 */
export function mapStreamOfferToCard(
  offers: HotelOffer[],
  info?: HotelSearchInfo,
  searchToken?: string,
  fallbackDestination?: string,
): HotelOfferCardModel[] {
  return offers.map((offer) => {
    const detail = mapOfferToDetailHotel(offer, { info, searchToken, fallbackDestination });

    const roomType =
      detail.roomOptions[0]?.type &&
      detail.roomOptions[0].type !== 'hotels.detail.roomOptionFallback'
        ? detail.roomOptions[0].type
        : undefined;

    // Pick the promotion label from the cheapest rate that has one.
    const promotionLabel = detail.roomOptions
      .flatMap((room) => room.rates)
      .sort((a, b) => a.sale - b.sale)
      .find((rate) => rate.promotionLabel)?.promotionLabel;

    return {
      hotelId: offer.hotelId,
      hotelName: detail.name,
      supplier: offer.supplier,
      currency: offer.currency,
      totalPrice: detail.totalPrice,
      rateSummary: detail.rateSummary,
      rateFeatures: detail.rateFeatures,
      stars: detail.stars,
      imageUrl: detail.imageUrl || undefined,
      description: detail.description,
      address: detail.address || undefined,
      roomType,
      hotelTypeId: detail.hotelTypeId,
      availableMealTypes: detail.availableMealTypes,
      hasPromotion: detail.hasPromotion,
      promotionLabel,
      amenities: detail.amenities,
      detail,
    };
  });
}
